import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications, markAsRead, readAll, type NotificationDto,
} from '../../api/notifications';
import { useNotificationStore } from '../../store/notificationStore';
import { useProfileUiStore } from '../../store/profileUiStore';
import { useChatStore } from '../../store/chatStore';
import { notifRoute } from '../../lib/notification-route';
import { useModalLeave } from '../../hooks/useModalLeave';
import { CheckCheck, Bell, Inbox, Settings, Loader2, ArrowRight } from '../../icons';
import {
  notifMeta, NOTIF_CATEGORY_KEYS, NOTIF_CATEGORY_META, type NotifCategoryKey,
} from '../../lib/notification-meta';
import {
  tNotif, formatNotifTime, getNotifLang, subscribeNotifLang,
} from '../../lib/notification-i18n';
import './NotificationPanel.css';

/**
 * 알림 팝업 (2026-08-18) — 사용자 요구:
 *   "상태바의 알림을 클릭하면 내 프로필의 내 알림으로 이동하지 말고 팝업으로 오픈한다.
 *    왼쪽에는 전체알림, 안읽은 알림, 메뉴별 알림 - 메뉴들이 나오고 해당 메뉴별 알림이
 *    보여진다. 설정 및 관리를 누르면 프로필 설정의 알림설정으로 화면이 보인다."
 *
 * 그전에는 벨을 누르면 **안읽음 8건만** 보이는 좁은 드롭다운이 뜨고, 나머지를 보려면
 * '전체 보기' 로 `/me?tab=notifications` 페이지로 **떠나야** 했다. 보던 화면을 잃지 않고
 * 알림을 훑는 것이 요구의 핵심이라, 좌측 분류 + 우측 목록을 한 팝업에 담는다.
 *
 * ## 메뉴별 분류는 클라이언트가 한다
 *
 * 서버 inbox 는 `isRead`·`limit`·`cursor` 만 받는다 — **카테고리 필터가 없다.** 그래서
 * 받아 온 목록을 `notifMeta(type, source)` 로 갈라 센다(목록·드롭다운과 같은 SSOT).
 * 따라서 좌측 개수는 **불러온 범위 안의 수**다. 한 번에 {@link PAGE} 건을 받고 '더 보기'
 * 로 이어 받으며, 받을수록 개수도 함께 자란다. 서버에 카테고리 필터가 생기면 그때
 * 좌측 선택을 질의로 넘기면 된다(그 전까지 전수 집계는 못 한다 — 알림이 수천 건이면
 * 전부 받아 세는 쪽이 더 나쁘다).
 *
 * ## 안 읽은 수만은 서버 숫자를 쓴다
 *
 * 배지와 어긋나면 안 되므로 '안 읽은 알림' 칸의 수는 **store 의 unreadCount**(서버 집계)를
 * 그대로 쓴다. 나머지 칸은 위에서 말한 '불러온 범위' 기준이다.
 */

/** 한 번에 받아 오는 건수 — 서버 상한은 200. */
const PAGE = 60;

type Filter = { kind: 'all' } | { kind: 'unread' } | { kind: 'cat'; key: NotifCategoryKey };

interface Props {
  open: boolean;
  onClose: () => void;
  /** 여는 방향 — 'up'(하단 상태바 위로) / 'down'(상단 바 아래). */
  placement?: 'up' | 'down';
}

function sameFilter(a: Filter, b: Filter): boolean {
  return a.kind === b.kind && (a.kind !== 'cat' || b.kind !== 'cat' || a.key === b.key);
}

export function NotificationPanel({ open, onClose, placement = 'up' }: Props): JSX.Element | null {
  useSyncExternalStore(subscribeNotifLang, getNotifLang, getNotifLang);

  const [items, setItems] = useState<NotificationDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>({ kind: 'all' });

  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const decrementUnread = useNotificationStore((s) => s.decrementUnread);
  const setUnread = useNotificationStore((s) => s.setUnread);
  const lastBump = useNotificationStore((s) => s.lastNotificationBump);
  const openSettings = useProfileUiStore((s) => s.openSettings);
  const setActiveChat = useChatStore((s) => s.setActiveChat);
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const { shouldRender, isLeaving } = useModalLeave(open, 160);

  /**
   * 목록은 **전체를 한 벌만** 받는다(안읽음·메뉴별은 이 한 벌을 걸러 보여 준다).
   * 필터마다 따로 받으면 같은 알림이 필터를 옮길 때마다 다시 날아오고, 좌측 개수도
   * 필터에 따라 달라져 보인다.
   */
  const load = useCallback(async (c: string | null) => {
    if (c) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res = await fetchNotifications({ limit: PAGE, ...(c ? { cursor: c } : {}) });
      const got = res.notifications ?? [];
      setItems((prev) => (c ? [...prev, ...got] : got));
      setCursor(res.nextCursor ?? null);
    } catch {
      setError(tNotif('notif.error'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // 열 때 · 새 알림이 올 때(lastBump) 첫 페이지를 다시 읽는다.
  useEffect(() => {
    if (!open) return;
    void load(null);
  }, [open, lastBump, load]);

  // 바깥 클릭 닫기 — 여는 클릭이 곧바로 바깥 클릭으로 잡히지 않게 한 틱 미룬다.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', onPointer), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onPointer); };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /** 메뉴별 개수 — 불러온 범위 기준(파일 머리 주석). */
  const byCategory = useMemo(() => {
    const acc = new Map<NotifCategoryKey, number>();
    for (const n of items) {
      const key = notifMeta(n.type, n.source).categoryKey;
      acc.set(key, (acc.get(key) ?? 0) + 1);
    }
    return acc;
  }, [items]);

  const shown = useMemo(() => {
    if (filter.kind === 'unread') return items.filter((n) => !n.isRead);
    if (filter.kind === 'cat') {
      return items.filter((n) => notifMeta(n.type, n.source).categoryKey === filter.key);
    }
    return items;
  }, [items, filter]);

  /**
   * 항목을 누르면 **읽음 + 그 알림이 가리키는 곳으로 이동**한다 (2026-08-18 2차 요구).
   *
   * 갈 곳은 `lib/notification-route` 가 정한다. 갈 곳이 없는 종류(결재는 라우트 미등록,
   * 시스템 알림은 목적지 자체가 없다)면 **읽음만** 하고 팝업을 닫지 않는다 — 눌렀는데
   * 아무 일도 없이 팝업만 사라지면 고장으로 보인다.
   */
  const handleItemClick = useCallback(async (n: NotificationDto) => {
    if (!n.isRead) {
      try {
        const updated = await markAsRead(n.id);
        decrementUnread();
        setItems((prev) => prev.map((x) => (x.id === n.id ? updated : x)));
      } catch { /* silent — 이동은 읽음 실패와 무관하게 계속한다 */ }
    }

    const route = notifRoute(n);
    if (!route) return;
    // 채팅방은 경로가 아니라 store 로 고른다(/chat/:id 라우트가 없다).
    if (route.chatRoomId) setActiveChat(route.chatRoomId);
    onClose();
    navigate(route.to);
  }, [decrementUnread, navigate, onClose, setActiveChat]);

  const handleReadAll = useCallback(async () => {
    try {
      await readAll();
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => (n.isRead ? n : { ...n, isRead: true, readAt: now })));
      setUnread(0);
    } catch { /* silent */ }
  }, [setUnread]);

  if (!shouldRender) return null;

  const railItem = (f: Filter, label: string, count: number, Icon: typeof Bell, accent?: string) => {
    const active = sameFilter(filter, f);
    return (
      <button
        key={label}
        type="button"
        className={`notif-panel__rail-item${active ? ' is-active' : ''}`}
        aria-current={active || undefined}
        style={accent ? ({ '--n-accent': accent } as CSSProperties) : undefined}
        onClick={() => setFilter(f)}
      >
        <Icon size={14} aria-hidden />
        <span className="notif-panel__rail-label">{label}</span>
        {count > 0 && <span className="notif-panel__rail-count">{count > 99 ? '99+' : count}</span>}
      </button>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`notif-panel notif-panel--${placement}${isLeaving ? ' notif-panel--leaving' : ''}`}
      role="dialog"
      aria-label={tNotif('notif.aria_list')}
      aria-hidden={isLeaving || undefined}
      // 벨 <button> 의 형제로 렌더되지만, 안쪽 클릭이 상태바 토글로 새는 것을 막는다.
      onClick={(e) => e.stopPropagation()}
    >
      <header className="notif-panel__head">
        <h3 className="notif-panel__title">{tNotif('notif.title')}</h3>
        {/*
          「더보기」 — 설정 > **내 알림**(전체 목록)으로 간다 (2026-09-07 사용자 지시).
          이 팝업은 «최근» 만 보여 준다. 전체 이력은 옛 `/me?tab=notifications` 에 있었는데,
          그 화면을 없애면서 설정 안으로 들어왔다.
          ⚠ 아래 footer 의 「설정 및 관리」와 **가는 곳이 다르다** — 저쪽은 «어떤 알림을 받을지»
            (설정 > 알림설정), 이쪽은 «받은 알림 목록»(설정 > 내 알림) 이다. 설정 모달에서도
            두 자리가 갈라져 있다(내 정보 관리 다음이 내 알림, 알림설정은 그 아래).
        */}
        <div className="notif-panel__headacts">
          <button
            type="button"
            className="notif-panel__openinbox"
            onClick={() => { onClose(); openSettings('inbox'); }}
          >
            {tNotif('notif.panel.open_inbox')} <ArrowRight size={13} aria-hidden />
          </button>
          <button
            type="button"
            className="notif-panel__readall"
            onClick={handleReadAll}
            disabled={unreadCount === 0}
          >
            <CheckCheck size={13} aria-hidden /> {tNotif('notif.read_all')}
          </button>
        </div>
      </header>

      <div className="notif-panel__body">
        <nav className="notif-panel__rail scrollbar-thin" aria-label={tNotif('notif.panel.nav_aria')}>
          {railItem({ kind: 'all' }, tNotif('notif.panel.all'), items.length, Inbox)}
          {railItem({ kind: 'unread' }, tNotif('notif.panel.unread'), unreadCount, Bell)}

          <div className="notif-panel__rail-sep" role="separator" />
          <div className="notif-panel__rail-label-head">{tNotif('notif.panel.by_menu')}</div>

          {/* 알림이 하나도 없는 메뉴는 세우지 않는다 — 눌러도 빈 목록만 나오는 칸이 된다. */}
          {NOTIF_CATEGORY_KEYS.filter((k) => (byCategory.get(k) ?? 0) > 0).map((k) => railItem(
            { kind: 'cat', key: k },
            tNotif(k),
            byCategory.get(k) ?? 0,
            NOTIF_CATEGORY_META[k].Icon,
            NOTIF_CATEGORY_META[k].accent,
          ))}
        </nav>

        <div className="notif-panel__list scrollbar-thin">
          {loading && <div className="notif-panel__status">{tNotif('notif.loading')}</div>}
          {error && <div className="notif-panel__status notif-panel__status--error">{error}</div>}

          {!loading && !error && shown.length === 0 && (
            <div className="notif-panel__empty">
              <Bell size={26} aria-hidden />
              {filter.kind === 'unread'
                ? tNotif('notif.empty_unread')
                : filter.kind === 'cat' ? tNotif('notif.panel.empty_cat') : tNotif('notif.empty')}
            </div>
          )}

          {!loading && !error && shown.map((n, i) => {
            const meta = notifMeta(n.type, n.source);
            const Icon = meta.Icon;
            return (
              <button
                key={n.id}
                type="button"
                className={`notif-panel__item${n.isRead ? '' : ' is-unread'}`}
                style={{ '--n-accent': meta.accent, animationDelay: `${Math.min(i, 8) * 24}ms` } as CSSProperties}
                onClick={() => void handleItemClick(n)}
                aria-label={tNotif('notif.aria_item', {
                  title: n.title,
                  unread: n.isRead ? '' : tNotif('notif.aria_item_unread'),
                })}
              >
                <span className="notif-panel__item-ico" aria-hidden><Icon size={15} /></span>
                <span className="notif-panel__item-body">
                  <span className="notif-panel__item-title">{n.title}</span>
                  <span className="notif-panel__item-meta">
                    {tNotif(meta.categoryKey)} · {formatNotifTime(n.createdAt)}
                  </span>
                </span>
                {!n.isRead && <span className="notif-panel__dot" aria-hidden />}
              </button>
            );
          })}

          {/* 더 받으면 좌측 개수도 함께 자란다(불러온 범위 기준이라서). */}
          {!loading && !error && cursor && (
            <button
              type="button"
              className="notif-panel__more"
              onClick={() => void load(cursor)}
              disabled={loadingMore}
            >
              {loadingMore ? <Loader2 size={13} className="notif-panel__spin" aria-hidden /> : null}
              {tNotif('notif.more')}
            </button>
          )}
        </div>
      </div>

      <footer className="notif-panel__foot">
        <button
          type="button"
          className="notif-panel__settings"
          onClick={() => { onClose(); openSettings('notifications'); }}
        >
          <Settings size={13} aria-hidden /> {tNotif('notif.panel.settings')}
        </button>
      </footer>
    </div>
  );
}
